import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  MessageSquare,
  Receipt,
  Calendar,
  Video,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Users,
  PenLine,
  Clock,
  RefreshCw,
  Award,
  Building2,
  GitBranch,
} from 'lucide-react';
import useAuthStore from '../../context/authStore';
import useClientServices from '../../hooks/useClientServices';
import NotificationPanel from '../notifications/NotificationPanel';
import { NavigationNotificationBadge, useNavigationNotifications } from '../../hooks/useNavigationNotifications';
import { BRAND } from '../../config/branding';
import { hasUserModuleAccess, canAccessBillingWorkspace, CLIENT_PORTAL_MODULES } from '../../utils/clientPortalPermissions';

// Navigation is generated per-user (role-based) inside the component to avoid referencing `user` before it's available



export default function ClientLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuthStore();
  const { services, refetch: refetchServices } = useClientServices();

  // Build navigation in stages: prominent links first, a SERVICES section header, then service-specific items.
  const navigation = [];

  // --- prominent entries ---
  navigation.push({ name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard });
  if (user?.role !== 'worker') {
    navigation.push({ name: 'Messages', href: '/messages', icon: MessageSquare });
  }
  if (canAccessBillingWorkspace(user)) {
    navigation.push({ name: 'Billing & Subscriptions', href: '/invoices', icon: Receipt });
  }

  // build service-based groups so each gets its own subheader
  const consultingItems = [];
  const staffingItems = [];
  const lmsItems = [];
  const signatureItems = [];
  const utilityItems = [];

  // Consulting (projects plus documents)
  if (
    services?.consulting?.enabled &&
    user &&
    ['org_admin', 'org_team', 'facility_admin', 'facility_manager'].includes(user.role) &&
    hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.consulting_workspace, 'view')
  ) {
    consultingItems.push({ name: 'Projects', href: '/projects', icon: FolderKanban });
    consultingItems.push({ name: 'Documents', href: '/documents', icon: FileText });
    // signature will get its own header below
    signatureItems.push({ name: 'Signatures', href: '/signatures', icon: PenLine });
  }

  // Staffing
  if (
    services?.staffing?.enabled &&
    ['org_admin', 'org_team', 'facility_admin', 'facility_manager'].includes(user?.role) &&
    hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.staffing_requests, 'view')
  ) {
    staffingItems.push({ name: 'Staffing Requests', href: '/staffing/requests', icon: Users });
    if (
      ['facility_admin', 'facility_manager'].includes(user?.role) &&
      hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.staffing_timesheet_approvals, 'view')
    ) {
      staffingItems.push({ name: 'Timesheet Approvals', href: '/staffing/timesheet-approvals', icon: Clock });
    }
    if (hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.staffing_rate_approvals, 'view')) {
      staffingItems.push({ name: 'Rate Approvals', href: '/staffing/rate-approvals', icon: Clock });
    }
  }

  // Training / LMS
  if (services?.lms?.enabled || user?.role === 'worker') {
    lmsItems.push({ name: 'Training Catalog', href: '/training', icon: Video });
    lmsItems.push({ name: 'Learning Paths', href: '/learning-paths', icon: GitBranch });
    // certificates page for learners and admins
    lmsItems.push({ name: 'Certificates', href: '/certificates', icon: Award });
    if (['org_admin', 'org_team', 'facility_admin', 'facility_manager'].includes(user?.role) && hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.lms_management, 'view')) {
      lmsItems.push({ name: 'Training Management', href: '/training-management', icon: Video });
      lmsItems.push({ name: 'Course Development', href: '/lms/course-dev', icon: Video });
    }
  }

  // Calendar is a utility rather than a service
  utilityItems.push({ name: 'Calendar', href: '/calendar', icon: Calendar });

  // if there are utility items, insert them immediately after billing (no header)
  if (utilityItems.length) {
    navigation.push(...utilityItems);
  }

  // combine service groups; each already includes its own header
  if (
    consultingItems.length ||
    staffingItems.length ||
    lmsItems.length ||
    signatureItems.length
  ) {
    if (consultingItems.length) {
      navigation.push({ section: 'Consulting' });
      navigation.push(...consultingItems);
    }
    if (staffingItems.length) {
      navigation.push({ section: 'Staffing' });
      navigation.push(...staffingItems);
    }
    if (lmsItems.length) {
      navigation.push({ section: 'LMS' });
      navigation.push(...lmsItems);
    }
    if (signatureItems.length) {
      navigation.push({ section: 'Signature' });
      navigation.push(...signatureItems);
    }
  }

  const notificationHrefs = [
    ...navigation.filter((item) => item.href).map((item) => item.href),
    '/team', '/facilities', '/settings',
  ];
  const { badgeFor } = useNavigationNotifications(token, 'client', notificationHrefs);


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRefreshServices = async () => {
    setIsRefreshing(true);
    try {
      await refetchServices();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500); // Keep spinning for at least 500ms for visual feedback
    }
  };

  const isActive = (href) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    // Exact match or match with trailing slash to avoid partial path matches
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-secondary-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-secondary-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/stratel-favicon-dark.png" alt={BRAND.name} className="w-12 h-12 object-contain" onError={(e) => { e.currentTarget.src = '/branding/stratel-logo-placeholder.svg'; }} />
              <div>
                <span className="font-display font-bold text-secondary-900">{BRAND.name}</span>
                <span className="block text-xs text-secondary-500">Client Portal</span>
              </div>
            </Link>
            <button
              className="lg:hidden text-secondary-400 hover:text-secondary-600"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item, idx) => {
              // section header indicates a logical grouping
              if (item.section) {
                return (
                  <div key={`section-${idx}`} className="pt-4 pb-2">
                    <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider">
                      {item.section}
                    </p>
                  </div>
                );
              }

              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                  }`}
                >
                  <Icon size={20} />
                  <span className="flex-1">{item.name}</span>
                  <NavigationNotificationBadge badge={badgeFor(item.href)} />
                </Link>
              );
            })}

            <div className="pt-4 pb-2">
              <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider">
                Account
              </p>
            </div>
            {hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.team_management, 'view') && (
              <Link
                to="/team"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/team')
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                }`}
              >
                <Users size={20} />
                <span className="flex-1">Team</span>
                <NavigationNotificationBadge badge={badgeFor('/team')} />
              </Link>
            )}
            {user?.role === 'org_admin' && hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.organization_settings, 'view') && (
              <Link
                to="/facilities"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/facilities')
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                }`}
              >
                <Building2 size={20} />
                <span className="flex-1">Facilities</span>
                <NavigationNotificationBadge badge={badgeFor('/facilities')} />
              </Link>
            )}
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/settings')
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
              }`}
            >
              <Settings size={20} />
              Settings
            </Link>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-secondary-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-secondary-500 truncate">
                  {user?.organizationId?.name || 'Client'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-secondary-400 hover:text-secondary-600 rounded-lg hover:bg-secondary-100"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-[70] bg-white border-b border-secondary-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              className="lg:hidden p-2 text-secondary-600 hover:bg-secondary-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshServices}
                disabled={isRefreshing}
                className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh service settings"
              >
                <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
              </button>
              <NotificationPanel />

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 text-secondary-600 hover:bg-secondary-100 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-sm font-medium">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <ChevronDown size={16} />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-20">
                      <div className="px-4 py-2 border-b border-secondary-100">
                        <p className="text-sm font-medium text-secondary-900">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-secondary-500">{user?.email}</p>
                      </div>
                      <Link to="/settings" className="block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50" onClick={() => setUserMenuOpen(false)}>
                        Settings
                      </Link>
                      <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
